import {
    API_ENTRY_POINT,
    GET_ACCOR_COUNTRY_LANGUAGE,
    SET_ACCOR_COUNTRY_LANGUAGE_MANUAL,
    STORAGE_EXPIRATION,
    LOCALSTORAGE_NAME,
    ACCOR_COUNTRY_CODE_PARAM,
    LANGUAGE_CODE_PARAM,
    SITE_CODE_PARAM,
    SITE_CODE,
    API_KEY
  } from "../common/constants";
  import { fetchAbstract } from "../common/utils";
  import { StorageService } from "../../storage";
  
  export class PosCountryLanguage {
    constructor() {
      if (PosCountryLanguage._instance) {
        return PosCountryLanguage._instance;
      }
      this.formattedData = {
        continents: [],
        geolocOptionsData: [],
      };
      this.apiKey = null;
      this.apiEntryPoint = null;
      this.session = StorageService.getInstance();
      PosCountryLanguage._instance = this;
    }
  
    async getAccorCountryLanguage(siteCode = SITE_CODE) {
      try {
        const sessionData = this.session.get(LOCALSTORAGE_NAME);
        if (sessionData) {
          return sessionData;
        }
        const rawData = await fetchAbstract(
          `${this.apiEntryPoint || API_ENTRY_POINT}${GET_ACCOR_COUNTRY_LANGUAGE}?${SITE_CODE_PARAM}=${siteCode}`,
          this.apiKey || API_KEY
        );
        this.formatData(rawData);
        const expiration = new Date().getTime() + STORAGE_EXPIRATION;
        this.session.set(LOCALSTORAGE_NAME, this.formattedData, expiration);
        return this.formattedData;
      } catch (error) {
        throw new Error(
          `An error occured in getAccorCountryLanguage func : ${error}`
        );
      }
    }
  
    extractContinent(continent) {
      if (!continent) {
        throw new TypeError(
          "Func extractContinent : continent argument was not provided"
        );
      }
      if (continent.continentLabel) {
        this.formattedData["continents"].push(continent.continentLabel);
      }
    }
  
    setAttributes(index, continent) {
      this.formattedData.geolocOptionsData[index].attributes = [
        {
          "data-id": continent.continentLabel,
          lang: continent.continentCode !== "europe" ? "en" : "fr",
        },
      ];
    }
  
    buildOptions(index, continent) {
      for (const pointOfSale of continent.countryList) {
        const POS = this.buildPOS(pointOfSale, continent);
        this.formattedData.geolocOptionsData[index].options.push(POS);
      }
    }
  
    buildPOS(pointOfSale, continent) {
      if (!pointOfSale || !continent) {
        throw new TypeError("Func buildPOS : an argument was not provided");
      }
      return {
        defaultUrl: pointOfSale.homepageUrl,
        originalText: pointOfSale.accorCountryLabel,
        text: pointOfSale.accorCountryLanguageLabel,
        value: `${pointOfSale.languageCode}_${pointOfSale.accorCountryCode}`,
        continent: continent.continentLabel,
        contribzone: pointOfSale.pointOfSale,
        country: pointOfSale.accorCountryCode,
        countryCode: pointOfSale.isoCountryCode[0],
        lang: pointOfSale.languageCode,
        languages: pointOfSale.languageLabel,
        attributes: [
          {
            "data-continent": continent.continentLabel,
            "data-contribzone": pointOfSale.pointOfSale,
            "data-country": pointOfSale.accorCountryCode,
            "data-lang": pointOfSale.languageCode,
            lang: pointOfSale.languageCode,
          },
        ],
      };
    }
  
    formatData(rawData) {
      if (!rawData) {
        throw new TypeError(
          "Func formatData : rawdata argument was not provided"
        );
      }
      let index = 0;
      for (const continent of rawData) {
        this.extractContinent(continent);
        this.formattedData.geolocOptionsData[index] = {
          text: continent.continentLabel,
          value: continent.continentLabel,
        };
        this.setAttributes(index, continent);
        this.formattedData.geolocOptionsData[index].options = [];
        this.buildOptions(index, continent);
        index++;
      }
    }
  
    async updateGeoloc(accorCountryCode, languageCode, siteCode = SITE_CODE) {
      if (!accorCountryCode || !languageCode || !siteCode) {
        throw new TypeError(`Func updateGeoloc : an argument was not provided`);
      }
      try {
        return await fetchAbstract(
          `${this.apiEntryPoint || API_ENTRY_POINT}${SET_ACCOR_COUNTRY_LANGUAGE_MANUAL}?${ACCOR_COUNTRY_CODE_PARAM}=${accorCountryCode}&${LANGUAGE_CODE_PARAM}=${languageCode}&${SITE_CODE_PARAM}=${siteCode}`,
          this.apiKey || API_KEY
        );
      } catch (error) {
        throw new Error(`Func updateGeoloc, an error occured : ${error} `);
      }
    }
  
    async getHomepageUrl(accorCountryCode, languageCode, siteCode = SITE_CODE) {
      if (!accorCountryCode || !languageCode || !siteCode) {
        throw new TypeError(`Func getHomepageUrl : an argument was not provided`);
      }
      let data = this.formattedData;
      if (!data.geolocOptionsData.length) {
        data = await this.getAccorCountryLanguage(SITE_CODE);
      }
      let homepageUrl = null;
      for (const continent of data.geolocOptionsData) {
        const homePageCountry = continent.options.find(
          (cty) => cty.country === accorCountryCode && cty.lang === languageCode
        );
        if (homePageCountry) {
          homepageUrl = homePageCountry.defaultUrl;
          break;
        }
      }
      return homepageUrl;
    }
  
    async getContinentFromCountry(countryParam) {
      if (!countryParam) {
        throw new TypeError(
          `Func getContinentFromCountry : countryParam argument was not provided`
        );
      }
      let data = this.formattedData;
      if (!data.geolocOptionsData.length) {
        data = await this.getAccorCountryLanguage(SITE_CODE);
      }
      let country = null;
      for (const continent of data.geolocOptionsData) {
        const countryInContinent = continent.options.find(
          (cty) => cty.country === countryParam
        );
        if (countryInContinent) {
          country = {
            continentCode: countryInContinent.contribzone,
            continentLabel: countryInContinent.continent,
          };
          break;
        }
      }
      return country;
    }
  
    async isLangInCountry(displayzone, lang) {
      if (!displayzone || !lang) {
        throw new TypeError(
          `Func isLangInCountry : an argument was not provided`
        );
      }
      let data = this.formattedData;
      if (!data.geolocOptionsData.length) {
        data = await this.getAccorCountryLanguage(SITE_CODE);
      }
  
      let countryLang = null;
      for (const continent of data.geolocOptionsData) {
        const countryAndLangInContinent = continent.options.find(
          (cty) => cty.country === displayzone && cty.lang === lang
        );
        if (countryAndLangInContinent) {
          countryLang = countryAndLangInContinent;
          break;
        }
      }
      return countryLang;
    }
  
    static getInstance() {
      return PosCountryLanguage._instance || new PosCountryLanguage();
    }
  }
  